import { FC, useState, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { orderFeedbackEvent } from '../tracking/orderFeedbackTracking';
import { Link } from 'dibs-elements/exports/Link';
import { StarRating } from 'dibs-elements/exports/StarRating';
import { SellerReviewsModalAsync } from 'dibs-seller-reviews-modal/exports/SellerReviewsModalAsync';

import { useSellerRating } from '../../shared/hooks/useSellerRating';

import { PdpRespSidebarSellerInfoRating_seller$key } from './__generated__/PdpRespSidebarSellerInfoRating_seller.graphql';

import styles from './PdpRespSidebarSellerInfoRating.scss';

const sellerFragment = graphql`
    fragment PdpRespSidebarSellerInfoRating_seller on Seller {
        serviceId
        ...useSellerRating_seller
    }
`;

const SellerRating: FC<{ rating: number }> = ({ rating }) => {
    return (
        <div className={styles.wrapper}>
            {rating > 0 && (
                <span className={styles.rating}>{(Math.round(rating * 100) / 100).toFixed(1)}</span>
            )}
            <StarRating rating={rating} size="x-small" />
        </div>
    );
};

const copy = defineMessages({
    sellerReviews: {
        id: 'PdpRespSidebarSellerInfoRating.sellerReviews',
        defaultMessage: 'see seller reviews',
    },
});

export const PdpRespSidebarSellerInfoRating: FC<{
    seller: PdpRespSidebarSellerInfoRating_seller$key;
}> = ({ seller: sellerRef }) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const { serviceId: sellerId } = seller;

    const intl = useIntl();
    const rating = useSellerRating({ seller });
    const [isOpen, setIsOpen] = useState(false);

    const onOpen = useCallback(() => {
        setIsOpen(true);
        orderFeedbackEvent({
            label: 'pdp - sidebar',
            action: 'seller reviews expanded',
        });
    }, []);
    const onClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    return rating > 0 ? (
        <>
            <Link
                ariaLabel={intl.formatMessage(copy.sellerReviews)}
                onClick={onOpen}
                underline="none"
                dataTn="pdp-sidebar-seller-rating-link"
            >
                <SellerRating rating={rating} />
            </Link>

            <SellerReviewsModalAsync sellerId={sellerId} isOpen={isOpen} onClose={onClose} />
        </>
    ) : (
        <SellerRating rating={rating} />
    );
};
